class CartCheckboxUpsell {
  constructor(cartItemsInstance) {
    this.cartItemsInstance = cartItemsInstance;
    this.processingVariants = new Set();
    this.programmaticVariants = new Set();

    this.init();
  }

  init() {
    this.initEvents();
    this.initObserver();
    this.updateCheckboxState();
  }

  getCheckboxes() {
    return document.querySelectorAll('.cart-upsell__checkbox');
  }

  getCheckboxForVariant(variantId) {
    return document.querySelector(`.cart-upsell__checkbox[data-variant-id="${variantId}"]`);
  }

  getButtonForVariant(variantId) {
    return document.querySelector(`.cart-upsell__btn[data-variant-id="${variantId}"]`);
  }

  syncButtonState(variantId, checked) {
    const button = this.getButtonForVariant(variantId);
    if (button) {
      button.setAttribute('aria-pressed', checked ? 'true' : 'false');
    }
  }

  handleCheckboxChange(checkbox) {
    const variantId = checkbox.getAttribute('data-variant-id');
    if (!variantId) return;

    if (this.programmaticVariants.has(variantId)) {
      this.programmaticVariants.delete(variantId);
      this.syncButtonState(variantId, checkbox.checked);
      return;
    }

    if (this.processingVariants.has(variantId)) {
      checkbox.checked = !checkbox.checked;
      this.syncButtonState(variantId, checkbox.checked);
      return;
    }

    this.syncButtonState(variantId, checkbox.checked);

    if (checkbox.checked) {
      this.addToCart(variantId);
    } else {
      this.removeFromCart(variantId);
    }
  }

  initEvents() {
    document.body.addEventListener('click', (e) => {
      const button = e.target.closest('.cart-upsell__btn');
      if (!button) return;

      const variantId = button.getAttribute('data-variant-id');
      if (!variantId || this.processingVariants.has(variantId)) return;

      const checkbox = this.getCheckboxForVariant(variantId);
      if (!checkbox) return;

      e.preventDefault();
      checkbox.checked = !checkbox.checked;
      this.handleCheckboxChange(checkbox);
    });

    document.body.addEventListener('change', (e) => {
      if (!e.target.matches('.cart-upsell__checkbox')) return;

      e.stopPropagation();
      this.handleCheckboxChange(e.target);
    });

    document.addEventListener('cartUpdated', () => {
      setTimeout(() => this.updateCheckboxState(), 100);
    });
  }

  initObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const hasUpsellCheckbox = node.matches?.('.cart-upsell__checkbox')
              || node.querySelector?.('.cart-upsell__checkbox');

            if (hasUpsellCheckbox) {
              setTimeout(() => this.updateCheckboxState(), 0);
            }
          }
        });
      });
    });

    const target = document.querySelector('cart-drawer')
                || document.querySelector('cart-items')
                || document.querySelector('#main-cart-items')
                || document.body;

    observer.observe(target, { childList: true, subtree: true });
  }

  updateCheckboxState() {
    const checkboxes = this.getCheckboxes();
    if (!checkboxes.length) return;

    fetch(`${routes.cart_url}.js`)
      .then(r => r.json())
      .then(cart => {
        checkboxes.forEach(checkbox => {
          const variantId = checkbox.getAttribute('data-variant-id');
          if (!variantId || this.processingVariants.has(variantId) || !checkbox.isConnected) return;

          const hasUpsell = cart.items.some(item => item.variant_id.toString() === variantId);

          if (checkbox.checked !== hasUpsell) {
            this.programmaticVariants.add(variantId);
            checkbox.checked = hasUpsell;
          }

          this.syncButtonState(variantId, hasUpsell);
        });

        setTimeout(() => {
          this.programmaticVariants.clear();
        }, 50);
      })
      .catch(err => console.error('Error fetching cart:', err));
  }

  setLoading(variantId, isLoading) {
    const button = this.getButtonForVariant(variantId);
    if (button) {
      button.classList.toggle('loading', isLoading);
      button.disabled = isLoading;
    }
  }

  addToCart(variantId) {
    return this.changeUpsell(variantId, true);
  }

  removeFromCart(variantId) {
    return this.changeUpsell(variantId, false);
  }

  async requestCart(url, body) {
    const response = await fetch(url, body ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    } : { headers: { Accept: 'application/json' } });
    const data = await response.json();
    if (!response.ok || data.errors) {
      throw new Error(data.description || data.message || window.cartStrings.error);
    }
    return data;
  }

  async changeUpsell(variantId, adding) {
    if (this.processingVariants.has(variantId)) return;
    this.processingVariants.add(variantId);
    this.setLoading(variantId, true);
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = '';

    try {
      const sections = [...new Set(this.getSections().map(section => section.section))];
      const rendering = { sections, sections_url: window.location.pathname };
      let result;
      if (adding) {
        const checkbox = this.getCheckboxForVariant(variantId);
        const campaignId = checkbox?.closest('.cart-upsell__body')
          ?.querySelector('.dry-run-cart-upsell-campaign-id')?.value;
        const item = { id: variantId, quantity: 1 };

        if (campaignId) item.properties = { _dry_run_campaign_id: campaignId };

        result = await this.requestCart(routes.cart_add_url, { items: [item], ...rendering });
      } else {
        const cart = await this.requestCart(`${routes.cart_url}.js`);
        const item = cart.items.find(item => String(item.variant_id) === variantId);
        if (item) result = await this.requestCart(routes.cart_change_url, { id: item.key, quantity: 0, ...rendering });
      }
      await this.updateSections(result?.sections);
      document.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (err) {
      console.error('Error updating gift packaging:', err);
      const currentErrors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (currentErrors) currentErrors.textContent = err.message || window.cartStrings.error;
    } finally {
      this.processingVariants.delete(variantId);
      this.setLoading(variantId, false);
      this.updateCheckboxState();
    }
  }

  async updateSections(renderedSections = {}) {
    const sections = this.getSections();
    const htmlById = new Map();
    await Promise.all([...new Set(sections.map(section => section.section))].map(async id => {
      let html = renderedSections?.[id];
      if (!html) {
        const response = await fetch(`${routes.cart_url}?section_id=${encodeURIComponent(id)}`);
        if (!response.ok) throw new Error(window.cartStrings.error);
        html = await response.text();
      }
      htmlById.set(id, html);
    }));
    sections.forEach(section => {
      const html = htmlById.get(section.section);
      this.renderSection(section, new DOMParser().parseFromString(html, 'text/html'), html);
    });
  }

  renderSection(section, doc, html) {
    if (section.id === 'cart-drawer') {
      document.querySelector('cart-drawer')?.renderDrawerSection(html);
    } else if (section.id === 'main-cart-items') {
      const newContents = doc.querySelectorAll('.js-contents');
      const currentContents = document.querySelectorAll('#main-cart-items .js-contents');

      newContents.forEach((newContent, i) => {
        const current = currentContents[i];
        if (current && newContent) {
          current.innerHTML = newContent.innerHTML;
        }
      });
    } else if (section.id === 'cart-icon-bubble') {
      const container = document.querySelector(section.selector);
      const content = doc.querySelector('.shopify-section');
      if (container && content) {
        container.innerHTML = content.innerHTML;
      }
    } else {
      const newEl = doc.querySelector(section.selector);
      const currentEl = document.querySelector(section.selector);
      if (currentEl && newEl) {
        currentEl.innerHTML = newEl.innerHTML;
      }
    }
  }

  getSections() {
    const mainCartItems = document.getElementById('main-cart-items');
    const cartSections = ['main-cart-items', 'main-cart-header', 'main-cart-shipping', 'main-cart-discount', 'main-cart-total']
      .filter(() => mainCartItems)
      .map(id => ({
        id,
        section: mainCartItems.dataset.id,
        selector: id === 'main-cart-items' ? '.js-contents' : `#${id}`
      }));

    const drawerSections = document.querySelector('cart-drawer')
      ? [{ id: 'cart-drawer', section: 'cart-drawer' }]
      : [];
    return [...cartSections, ...drawerSections, { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' }];
  }
}

const createCartCheckboxUpsell = () => {
  if (window.cartUpsellInstance) return true;
  const cartInstance = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
  if (!cartInstance) return false;

  window.cartUpsellInstance = new CartCheckboxUpsell(cartInstance);

  if (typeof cartInstance.updateQuantity === 'function') {
    const originalUpdate = cartInstance.updateQuantity.bind(cartInstance);

    cartInstance.updateQuantity = function(...args) {
      const result = originalUpdate(...args);

      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cartUpdated'));
      }, 300);

      return result;
    };
  }

  return true;
};

const initializeCartCheckboxUpsell = () => {
  if (createCartCheckboxUpsell() || window.cartUpsellObserver) return;

  const observerTarget = document.querySelector('cart-drawer') || document.body;
  window.cartUpsellObserver = new MutationObserver(() => {
    if (!createCartCheckboxUpsell()) return;
    window.cartUpsellObserver.disconnect();
    window.cartUpsellObserver = null;
  });
  window.cartUpsellObserver.observe(observerTarget, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartCheckboxUpsell, { once: true });
} else {
  initializeCartCheckboxUpsell();
}
